<!-- ═══════════════════════════════════════════════════════════════
     NPI Provider Lookup Component
     Supports: autocomplete search via NIH API + manual fallback
     ═══════════════════════════════════════════════════════════════ -->

<div class="npi-lookup">

  <!-- Label -->
  <label class="npi-lookup__label">
    {{ label }}
    <span class="npi-lookup__label-badge" *ngIf="stateFilter">
      {{ stateFilter }} only
    </span>
  </label>

  <!-- Mode Toggle (search vs manual) -->
  <div class="npi-lookup__mode-toggle" *ngIf="allowManualEntry && !selectedProvider">
    <button
      type="button"
      class="npi-lookup__mode-btn"
      [class.npi-lookup__mode-btn--active]="mode === 'search'"
      (click)="setMode('search')">
      <i class="pi pi-search"></i>
      NPI Database
    </button>
    <button
      type="button"
      class="npi-lookup__mode-btn"
      [class.npi-lookup__mode-btn--active]="mode === 'manual'"
      (click)="setMode('manual')">
      <i class="pi pi-pencil"></i>
      Manual Entry
    </button>
  </div>

  <!-- ════════ SEARCH MODE ════════ -->
  <div *ngIf="mode === 'search' && !selectedProvider" class="npi-lookup__search">
    <p-autoComplete
      [(ngModel)]="searchText"
      [suggestions]="suggestions"
      (completeMethod)="onSearch($event)"
      (onSelect)="onSelect($event)"
      (onClear)="onClear()"
      [field]="'fullName'"
      [showClear]="true"
      [forceSelection]="false"
      [minLength]="2"
      [delay]="0"
      placeholder="Search by provider name, NPI, specialty, or city…"
      [style]="{ width: '100%' }"
      [inputStyle]="{ width: '100%' }"
      appendTo="body"
      [loading]="loading"
      [virtualScroll]="true"
      [virtualScrollItemSize]="68"
      [scrollHeight]="'480px'">

      <!-- Custom dropdown item template -->
      <ng-template let-provider pTemplate="item">
        <div class="npi-lookup__item">
          <div class="npi-lookup__item-icon">
            <i class="pi pi-user"></i>
          </div>
          <div class="npi-lookup__item-body">
            <div class="npi-lookup__item-name">
              {{ provider.fullName }}
              <span class="npi-lookup__item-credential" *ngIf="provider.credential">
                {{ provider.credential }}
              </span>
            </div>
            <div class="npi-lookup__item-meta">
              <span class="npi-lookup__item-type">{{ provider.providerType }}</span>
              <span class="npi-lookup__item-npi">NPI: {{ provider.npi }}</span>
            </div>
            <div class="npi-lookup__item-address" *ngIf="provider.practiceAddress">
              <i class="pi pi-map-marker"></i>
              {{ provider.practiceAddress }}
            </div>
          </div>
        </div>
      </ng-template>

      <!-- Empty state -->
      <ng-template pTemplate="empty">
        <div class="npi-lookup__empty">
          <span>No providers found</span>
          <button
            *ngIf="allowManualEntry"
            type="button"
            class="npi-lookup__empty-link"
            (click)="setMode('manual')">
            Enter manually instead
          </button>
        </div>
      </ng-template>

    </p-autoComplete>

    <small class="npi-lookup__hint">
      Search 7M+ providers — data from NIH/CMS, updated monthly
    </small>
  </div>

  <!-- ════════ MANUAL MODE ════════ -->
  <div *ngIf="mode === 'manual' && !selectedProvider" class="npi-lookup__manual">
    <div class="npi-lookup__manual-grid">

      <div class="npi-lookup__field npi-lookup__field--full">
        <label>Provider Name <span class="npi-lookup__required">*</span></label>
        <input
          type="text"
          pInputText
          [(ngModel)]="manualName"
          placeholder="Full name (e.g., Jane Smith, MD)"
        />
      </div>

      <div class="npi-lookup__field">
        <label>NPI Number <span class="npi-lookup__optional">(optional)</span></label>
        <input
          type="text"
          pInputText
          [(ngModel)]="manualNpi"
          placeholder="10-digit NPI"
          maxlength="10"
        />
      </div>

      <div class="npi-lookup__field">
        <label>Specialty / Type <span class="npi-lookup__optional">(optional)</span></label>
        <input
          type="text"
          pInputText
          [(ngModel)]="manualType"
          placeholder="e.g., Physician/Family Practice"
        />
      </div>

      <div class="npi-lookup__field">
        <label>Phone <span class="npi-lookup__optional">(optional)</span></label>
        <input
          type="text"
          pInputText
          [(ngModel)]="manualPhone"
          placeholder="(555) 123-4567"
        />
      </div>

      <div class="npi-lookup__field npi-lookup__field--action">
        <p-button
          label="Add Provider"
          icon="pi pi-check"
          [disabled]="!manualName.trim()"
          (onClick)="submitManualEntry()"
          styleClass="p-button-sm">
        </p-button>
      </div>
    </div>
  </div>

  <!-- ════════ SELECTED PROVIDER CARD ════════ -->
  <div *ngIf="selectedProvider" class="npi-lookup__selected">
    <div class="npi-lookup__selected-icon">
      <i class="pi pi-verified"></i>
    </div>

    <div class="npi-lookup__selected-body">
      <div class="npi-lookup__selected-name">
        {{ selectedProvider.fullName }}
        <span class="npi-lookup__selected-credential" *ngIf="selectedProvider.credential">
          {{ selectedProvider.credential }}
        </span>
      </div>

      <div class="npi-lookup__selected-details">
        <p-tag
          *ngIf="selectedProvider.providerType"
          [value]="selectedProvider.providerType"
          severity="info"
          [rounded]="true"
          styleClass="npi-lookup__tag">
        </p-tag>

        <span class="npi-lookup__selected-npi" *ngIf="selectedProvider.npi">
          NPI: {{ selectedProvider.npi }}
        </span>
      </div>

      <div class="npi-lookup__selected-meta" *ngIf="selectedProvider.practiceAddress || selectedProvider.phone">
        <span *ngIf="selectedProvider.practiceAddress">
          <i class="pi pi-map-marker"></i>
          {{ selectedProvider.practiceAddress }}
        </span>
        <span *ngIf="selectedProvider.phone">
          <i class="pi pi-phone"></i>
          {{ selectedProvider.phone }}
        </span>
      </div>
    </div>

    <button
      type="button"
      class="npi-lookup__remove"
      pTooltip="Remove provider"
      tooltipPosition="left"
      (click)="removeSelection()">
      <i class="pi pi-times"></i>
    </button>
  </div>

</div>
